import React, { useCallback, useState } from "react";
import { connect } from "react-redux";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";

import { StoreState } from "../../redux/configure-store";
import PageTitle from "../components/PageTitle";
import { Engagement, PaymentMethod } from "../types";
import { formatCurrencyAmount } from "../../common/utils";
import AdminTimesheetsTable from './AdminTimesheetsTable'
import AdminMissingTimesheets from './AdminMissingTimesheets'
import { backgrounds, borders } from "../../theme/theme"

type PaymentMethodStats = {
  paymentMethod: PaymentMethod;
  totalAmountOwed: number;
  numberOfActiveEngagements: number;
};

function AdminTimesheetsTablePage(props: any) {
  const { engagements } = props;

  const defaultPaymentMethodStats: PaymentMethodStats = {
    paymentMethod: "wise",
    totalAmountOwed: 0,
    numberOfActiveEngagements: 0,
  };

  const [numberOfDays, setNumberOfDays] = useState(22);
  const [numberOfDaysError, setNumberOfDaysError] = useState(false);
  const [numberOfActiveEngagements, setNumberOfActiveEngagements] = useState(0);
  const [totalAmountOwed, setTotalAmountOwed] = useState(0);
  const [bmoUsdStats, setBmoUsdStats] = useState<PaymentMethodStats>(
    defaultPaymentMethodStats
  );
  const [bitcoinStats, setBitcoinStats] = useState<PaymentMethodStats>(
    defaultPaymentMethodStats
  );
  const [wiseStats, setWiseStats] = useState<PaymentMethodStats>(
    defaultPaymentMethodStats
  );
  const [stablecoinStats, setStablecoinStats] = useState<PaymentMethodStats>(
    defaultPaymentMethodStats
  );

  function filterPaymentMethod(paymentMethod: PaymentMethod) {
    return function (engagement: Engagement): boolean {
      return engagement.user.paymentMethod === paymentMethod;
    };
  }

  const reduceEngagementPaymentAmount = useCallback((_numberOfDays: number) => {
    return function (acc: number, engagement: Engagement): number {
      return (
        ((engagement.hourlyRate * engagement.numberOfHoursPerWeek) / 5) *
        numberOfDays +
        acc
      );
    };
  }, [numberOfDays]);

  const setStats = useCallback(() => {
    if (!engagements || !engagements.length) {
      setNumberOfActiveEngagements(0);
      return;
    }
    const activeEngagements = engagements.filter((e: Engagement) => !e.endDate);
    setNumberOfActiveEngagements(activeEngagements.length);

    setTotalAmountOwed(
      activeEngagements.reduce(reduceEngagementPaymentAmount(numberOfDays), 0)
    );

    setBmoUsdStats({
      paymentMethod: "bmoUsd",
      totalAmountOwed: activeEngagements
        .filter(filterPaymentMethod("bmoUsd"))
        .reduce(reduceEngagementPaymentAmount(numberOfDays), 0),
      numberOfActiveEngagements: activeEngagements.filter(
        filterPaymentMethod("bmoUsd")
      ).length,
    });

    setWiseStats({
      paymentMethod: "wise",
      totalAmountOwed: activeEngagements
        .filter(filterPaymentMethod("wise"))
        .reduce(reduceEngagementPaymentAmount(numberOfDays), 0),
      numberOfActiveEngagements: activeEngagements.filter(
        filterPaymentMethod("wise")
      ).length,
    });

    setBitcoinStats({
      paymentMethod: "bitcoin",
      totalAmountOwed: activeEngagements
        .filter(filterPaymentMethod("bitcoin"))
        .reduce(reduceEngagementPaymentAmount(numberOfDays), 0),
      numberOfActiveEngagements: activeEngagements.filter(
        filterPaymentMethod("bitcoin")
      ).length,
    });

    setStablecoinStats({
      paymentMethod: "stablecoin",
      totalAmountOwed: activeEngagements
        .filter(filterPaymentMethod("stablecoin"))
        .reduce(reduceEngagementPaymentAmount(numberOfDays), 0),
      numberOfActiveEngagements: activeEngagements.filter(
        filterPaymentMethod("stablecoin")
      ).length,
    });
  }, [engagements, numberOfDays, reduceEngagementPaymentAmount]);

  React.useEffect(() => {
    setStats();
  }, [setStats]);

  function onChangeNumberOfDays(e: any) {
    const newValue = e.target.value;
    if (isNaN(newValue)) {
      setNumberOfDaysError(true);
      return;
    }
    setNumberOfDaysError(false);
    setNumberOfDays(e.target.value);
  }

  return (
    <div>
      <PageTitle>Timesheets</PageTitle>
      <Box sx={{ mb: 3, p: 2, backgroundColor: backgrounds.page, borderRadius: 1 }}>
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Projections
            </Typography>
            <TextField
              id="numberOfDays"
              name="numberOfDays"
              label="Number of Days"
              size="small"
              type="number"
              value={numberOfDays}
              onChange={onChangeNumberOfDays}
              error={numberOfDaysError}
              helperText={numberOfDaysError ? "Invalid number" : ""}
              sx={{ backgroundColor: "white" }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Totals
            </Typography>
            <Typography variant="body2">
              <strong>Total:</strong> {formatCurrencyAmount(totalAmountOwed)}
            </Typography>
            <Typography variant="body2">
              <strong>Active Engagements:</strong> {numberOfActiveEngagements}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={5}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              By Payment Method
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>BMO USD:</strong> {formatCurrencyAmount(bmoUsdStats.totalAmountOwed)} ({bmoUsdStats.numberOfActiveEngagements})
                </Typography>
                <Typography variant="body2">
                  <strong>Bitcoin:</strong> {formatCurrencyAmount(bitcoinStats.totalAmountOwed)} ({bitcoinStats.numberOfActiveEngagements})
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Stablecoin:</strong> {formatCurrencyAmount(stablecoinStats.totalAmountOwed)} ({stablecoinStats.numberOfActiveEngagements})
                </Typography>
                <Typography variant="body2">
                  <strong>Wise:</strong> {formatCurrencyAmount(wiseStats.totalAmountOwed)} ({wiseStats.numberOfActiveEngagements})
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <AdminMissingTimesheets
        engagements={props.engagements}
        timesheets={props.timesheets}
      />
      <Box>
        <Paper elevation={0} sx={{ width: "100%", overflowX: "auto", border: `1px solid ${borders.light}`, borderRadius: 2 }}>
          <AdminTimesheetsTable
            onRowClick={props.navigateToAdminTimesheetsViewPage}
            isDisableRowClick={false}
            timesheets={props.timesheets}
          >
          </AdminTimesheetsTable>
        </Paper>
      </Box>
    </div>
  );
}

const mapStateToProps = (storeState: StoreState) => ({
  engagements: storeState.admin.engagements,
  timesheets: storeState.admin.timesheets,
});

export default connect(
  mapStateToProps,
  {}
)(AdminTimesheetsTablePage);
